import { useEffect, useMemo, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import Header from 'components/Header'
import { UserAction } from 'models'
import {
  getUserActionsCount,
  getUserActionsByPaginationAndDate,
  downloadUserActionsExcel
} from 'api'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { UserActionType } from 'constant'
import { DataGrid, GridColDef } from '@mui/x-data-grid'

const getActionName = (action: string) => {
  switch (action) {
    case UserActionType.FulfillOrder:
      return 'Fulfill Order'
    case UserActionType.CreateOrder:
      return 'Create Order'
    case UserActionType.DeleteOrder:
      return 'Delete Order'
    case UserActionType.SyncOrder:
      return 'Sync Order'
    case UserActionType.RequestCancelOrder:
      return 'Request Cancel Order'
    case UserActionType.ConfirmCancelOrder:
      return 'Confirm Cancel Order'
    case UserActionType.RefuseCancelOrder:
      return 'Refuse Cancel Order'
    default:
      return action
  }
}
dayjs.extend(utc)

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50]

const UserActions = () => {
  const [userActions, setUserActions] = useState<UserAction[]>([])
  const [userActionsCount, setUserActionsCount] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20
  })
  const [fromDate, setFromDate] = useState<Dayjs | null>(null)
  const [toDate, setToDate] = useState<Dayjs | null>(null)
  const fromStr = useMemo(() => fromDate?.format('YYYY-MM-DD'), [fromDate])
  const toStr = useMemo(() => toDate?.format('YYYY-MM-DD'), [toDate])

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 250 },
    { field: 'username', headerName: 'Username', width: 150 },
    { field: 'company', headerName: 'Company', width: 150 },
    {
      field: 'action',
      headerName: 'Action',
      width: 150,
      valueFormatter: (params: string) => {
        if (!params) return ''
        return getActionName(params)
      }
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 1,
      minWidth: 300,
      renderCell: (params: any) => (
        <div
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: '1.5'
          }}
        >
          {params.value}
        </div>
      )
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 250,
      valueFormatter: (params: string) => {
        if (!params) return ''
        return dayjs(params).format('YYYY-MM-DD HH:mm:ss')
      }
    }
  ]

  const updateUserActions = async () => {
    setLoading(true)
    try {
      const countRes = await getUserActionsCount(fromStr, toStr)
      const total = countRes?.data?.count ?? 0
      setUserActionsCount(total)
      const listRes = await getUserActionsByPaginationAndDate(
        paginationModel.pageSize,
        paginationModel.page,
        fromStr,
        toStr
      )
      const list: UserAction[] = listRes?.data?.userActions ?? []
      const finalRows = list.map((r: any) => ({
        ...r,
        id: r.id ?? r._id ?? r.uuid
      }))
      setUserActions(finalRows)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    updateUserActions()
  }, [paginationModel.page, paginationModel.pageSize, fromStr, toStr])

  return (
    <>
      <Header />
      <Box
        sx={{
          display: 'flex',
          margin: '20px 30px'
        }}
      >
        <Button
          variant='contained'
          sx={{
            backgroundColor: '#607998',
            marginRight: '20px'
          }}
          disabled={!fromDate}
          onClick={async () => {
            try {
              const res = await downloadUserActionsExcel(fromStr, toStr)
              const blob = new Blob([res.data])
              const url = window.URL.createObjectURL(blob)
              const link = document.createElement('a')
              link.href = url
              link.setAttribute(
                'download',
                `User_Actions_From_${fromStr}_To_${toStr || dayjs().format('YYYY-MM-DD')}.xlsx`
              )
              document.body.appendChild(link)
              link.click()
              link.parentNode && link.parentNode.removeChild(link)
              window.URL.revokeObjectURL(url)
            } catch (error) {
              console.error('Export error:', error)
            }
          }}
        >
          Export User Actions
        </Button>
        <Box sx={{ marginRight: '20px' }}>
          <Typography>From</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={fromDate}
              maxDate={toDate || dayjs()}
              onChange={(newValue: any) => setFromDate(newValue)}
            />
          </LocalizationProvider>
        </Box>
        <Box>
          <Typography>To</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={toDate}
              minDate={fromDate ?? undefined}
              maxDate={dayjs()}
              disabled={!fromDate}
              onChange={(newValue: any) => setToDate(newValue)}
            />
          </LocalizationProvider>
        </Box>
      </Box>
      <Box sx={{ px: 3, pb: 3, '& .MuiDataGrid-row': { padding: '8px 0' } }}>
        <DataGrid
          rows={userActions}
          columns={columns}
          loading={loading}
          getRowId={row => row.id}
          disableRowSelectionOnClick
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode='server'
          rowCount={userActionsCount}
          getRowHeight={() => 'auto'}
          slots={{
            loadingOverlay: undefined
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton'
            }
          }}
          keepNonExistentRowsSelected={false}
        />
      </Box>
    </>
  )
}

export default UserActions
